/**
 * SectionCard — the single card/panel pattern for product surfaces.
 *
 * Replaces the ad-hoc `bg-… border … rounded-…` divs. Header wraps instead of
 * squeezing the title (the mobile defect found in the review), density is a
 * prop instead of a new component, and the three system states (loading, empty,
 * error) are handled here so callers stop reinventing them.
 */
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/system/states";

const PAD = {
  default: "p-5 sm:p-6",
  compact: "p-4",
};

export function SectionCard({
  title,
  description,
  actions,
  footer,
  density = "default",
  loading = false,
  error = null,
  onRetry = null,
  empty = false,
  emptyTitle = "Nessun dato",
  emptyDescription,
  emptyAction,
  headerContent,
  className = "",
  bodyClassName = "",
  testId,
  children,
  ...rest
}) {
  const pad = PAD[density] || PAD.default;
  const hasHeader = Boolean(title || description || actions || headerContent);

  let body;
  if (loading) {
    body = (
      <div className="space-y-3" data-testid={testId ? `${testId}-loading` : "section-card-loading"}>
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  } else if (error) {
    body = (
      <ErrorState
        description={typeof error === "string" ? error : undefined}
        onRetry={onRetry}
        testId={testId ? `${testId}-error` : "section-card-error"}
      />
    );
  } else if (empty) {
    body = (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
        testId={testId ? `${testId}-empty` : "section-card-empty"}
      />
    );
  } else {
    body = children;
  }

  return (
    <section
      data-testid={testId}
      className={`rounded-card border border-line-soft bg-surface shadow-card ${className}`}
      {...rest}
    >
      {hasHeader && (
        <header className={`${pad} pb-0 flex flex-wrap items-start justify-between gap-3`}>
          <div className="min-w-0">
            {title && (
              <h2 className="text-lg font-semibold text-content leading-tight">{title}</h2>
            )}
            {description && (
              <p className="text-sm text-content-muted mt-1 max-w-2xl">{description}</p>
            )}
          </div>
          {actions && (
            <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>
          )}
          {headerContent}
        </header>
      )}
      <div className={`${pad} ${hasHeader ? "pt-4" : ""} ${bodyClassName}`}>{body}</div>
      {footer && (
        <footer className={`${pad} pt-0 flex flex-wrap items-center gap-2`}>{footer}</footer>
      )}
    </section>
  );
}

export default SectionCard;
